





























































































































































































































































































import {
	computed,
	defineComponent,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { VueConstructor } from 'vue';
import Draggable from 'vuedraggable';
import { Routes } from '@front-app/constants/routes';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { getLearningProgramsStore } from '@front-app/modules/learningPrograms/store/LearningProgramsStore';
import { moveInternal } from '@front-app/modules/group/highlights/util/draggable';
import {
	LearningModule,
} from '@front-app/modules/learningPrograms/Model';
// eslint-disable-next-line
import { SortableEvent } from 'sortablejs';
import { Size, Spacing } from '@front-app/components/constants';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { SETTINGS_ICON } from '@front-app/vendor/icon-definitions';
import { FocusableElement, isFocusableElement, useAsyncModal } from '@front-app/hooks/useAsyncModal';
import SimpleCard from '@front-app/components/cards/SimpleCard.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import WarningCard from '@front-app/components/cards/state/WarningCard.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import AccreditedBadge from '@front-app/modules/learningPrograms/components/AccreditedBadge.vue';
import SlidingPanel from '@front-app/components/modals/SlidingPanel.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import PlusButton from '@front-app/components/buttons/PlusButton.vue';
import PublishInfo from '@front-app/modules/learningPrograms/admin/PublishInfo.vue';
import { DropdownPosition } from '@front-app/components/dropdowns/dropdownPosition';
import BackButton from '@front-app/components/buttons/BackButton.vue';
import DraggableModuleCard from '@front-app/modules/learningPrograms/admin/DraggableModuleCard.vue';
import {
	isModulePublished,
	isProgramAnyLoading,
	isProgramPublished,
	maxDateModified,
} from '@front-app/modules/learningPrograms/util/util';
import LastUpdateLoader from '@front-app/components/LastUpdateLoader.vue';
import CompletionCreditsBadge from '@front-app/modules/learningPrograms/components/CompletionCreditsBadge.vue';
import TotalCreditsBadge from '@front-app/modules/learningPrograms/components/TotalCreditsBadge.vue';
import { UpsertProgramRequest } from '@front-app/api-generated/learning-management-api';
import TrModal from '@front-app/components/modals/TrModal.vue';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import LearningMeta from '@front-app/modules/learningPrograms/components/LearningMeta.vue';
import { useTranslate } from '@front-app/hooks/useTranslate';
import SuccessCard from '@front-app/components/cards/state/SuccessCard.vue';
import { lmsAdminApi } from '@front-app/modules/learningPrograms/api/lms-api';
import CertificateIcon from '@front-app/modules/learningPrograms/components/CertificateIcon.vue';

const editProgramViewLoader = () => import(/* webpackChunkName: "editProgramModal" */ '@front-app/modules/learningPrograms/admin/edit/EditProgramForm.vue')
	.then((_) => _.default as VueConstructor);

const editModuleViewLoader = () => import(/* webpackChunkName: "editProgramModal" */ '@front-app/modules/learningPrograms/admin/ModuleCreateEditForm.vue')
	.then((_) => _.default as VueConstructor);

const editPartViewLoader = () => import(/* webpackChunkName: "editProgramModal" */ '@front-app/modules/learningPrograms/admin/PartCreateEditForm.vue')
	.then((_) => _.default as VueConstructor);

const editStepViewLoader = () => import(/* webpackChunkName: "editProgramModal" */ '@front-app/modules/learningPrograms/admin/StepCreateEditForm.vue')
	.then((_) => _.default as VueConstructor);

export default defineComponent({
	components: {
		Draggable,
		ErrorCard,
		SuccessCard,
		WarningCard,
		LoaderState,
		AccreditedBadge,
		SimpleCard,
		SlidingPanel,
		PlusButton,
		CtaButton,
		FontAwesomeIcon,
		PublishInfo,
		BackButton,
		DraggableModuleCard,
		LastUpdateLoader,
		CompletionCreditsBadge,
		TotalCreditsBadge,
		TrModal,
		LearningMeta,
		CertificateIcon,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
		programId: {
			type: String,
			required: true,
		},
	},
	setup(props, ctx) {
		// Initial setup
		const programsStore = getLearningProgramsStore();
		const program = computed(() => programsStore.getProgram(props.programId));
		const {
			track,
			isLoading: isLoadingPage,
			isError: isErrorPage,
		} = useAsyncStatus();
		watch(computed(() => props.programId), async (curr) => {
			await track(programsStore.fetchProgram(curr, true, true));
		}, { immediate: true });

		const areAnyModulesPublished = computed(() => program.value.modules.map(programsStore.getModule).some(isModulePublished));
		const showUnpublishedModuleWarning = computed(() => isProgramPublished(program.value) && !areAnyModulesPublished.value);

		// Program Editor
		const {
			isModalVisible: isEditProgramPanelVisible,
			modalView: editProgramView,
			showModal: openEditProgramModal,
			closeModal: closeEditProgramModal,
		} = useAsyncModal('', editProgramViewLoader);

		// Any program updates
		const isAnyLoading = isProgramAnyLoading(computed(() => props.programId), programsStore);
		const maxUpdateDate = maxDateModified(computed(() => props.programId), programsStore);

		// Module ordering
		const handleDragInput = (modules: string[]) => {
			programsStore.reorderProgramModules(props.groupId, props.programId, modules);
		};

		// Program publish info updates
		const {
			track: trackDateUpdate,
			isLoading: isDateLoading,
			isError: isDateError,
		} = useAsyncStatus();
		const pendingPublishData: Ref<null | UpsertProgramRequest> = ref(null);
		const isPublishPending = computed(() => pendingPublishData.value !== null);
		const cancelPendingPublish = () => {
			pendingPublishData.value = null;
		};
		const performProgramUpdate = (updates: UpsertProgramRequest) => trackDateUpdate(programsStore.updateProgram(props.groupId, props.programId, updates));
		const completePendingPublishProgramAndModules = async () => {
			const val = pendingPublishData.value;
			if (val === null || !val.publishDateStart) {
				return;
			}
			const moduleRequests = program.value.modules
				.map((id) => programsStore.updateModule(props.programId, id, { publishDateStart: val.publishDateStart }));
			await trackDateUpdate(Promise.all([
				programsStore.updateProgram(props.groupId, props.programId, val),
				Promise.all(moduleRequests),
			]));
			cancelPendingPublish();
		};
		const completePendingPublishProgramOnly = async () => {
			if (pendingPublishData.value === null) {
				return;
			}
			await performProgramUpdate(pendingPublishData.value);
			cancelPendingPublish();
		};
		const updateProgramDates = async (updates: UpsertProgramRequest) => {
			if (updates.publishDateStart === null || areAnyModulesPublished.value) {
				performProgramUpdate(updates);
			} else {
				pendingPublishData.value = updates;
			}
		};

		// Module Editor
		// NOTE: This must be a `Ref`, using `reactive` doesn't work with v-model and/or vue-draggable
		const {
			isModalVisible: isEditModulePanelVisible,
			modalView: editModuleView,
			showModal: openEditModuleModal,
			closeModal: closeEditModuleModal,
		} = useAsyncModal('', editModuleViewLoader);
		const selectedModuleId: Ref<null | string> = ref(null);
		const addModule = async (e: MouseEvent) => {
			selectedModuleId.value = null;
			openEditModuleModal(isFocusableElement(e.target) ? e.target : undefined);
		};
		const isSelectedModuleId = (moduleId: string): boolean => moduleId === selectedModuleId.value;
		const onUnchoose = (e: SortableEvent) => {
			const { newDraggableIndex } = e;
			if (newDraggableIndex && program.value.modules.length > newDraggableIndex) {
				const moduleId = program.value.modules[newDraggableIndex];
				selectedModuleId.value = moduleId;
			}
		};
		const deselectModule = (mod: LearningModule) => {
			if (isSelectedModuleId(mod.id)) {
				selectedModuleId.value = null;
			}
		};
		const $t = useTranslate(ctx);
		const deleteModule = (moduleId: string) => {
			// eslint-disable-next-line no-alert
			const shouldDelete = window.confirm($t('lms.admin.delete-module-confirmation') as unknown as string);
			if (!shouldDelete) {
				return;
			}
			deselectModule(programsStore.getModule(moduleId));
			programsStore.deleteModule(props.programId, moduleId);
		};

		// Part editor
		const {
			isModalVisible: isPartPanelVisible,
			modalView: partPanelView,
			showModal: showPartPanel,
			closeModal: closePartPanel,
		} = useAsyncModal('', editPartViewLoader);
		const selectedPartId: Ref<string | null> = ref('');
		const openEditPartModal = (opener: FocusableElement, partId: string | null, moduleId: string) => {
			selectedPartId.value = partId;
			selectedModuleId.value = moduleId;
			showPartPanel(opener);
		};

		// Step editor
		const {
			isModalVisible: isStepPanelVisible,
			modalView: stepPanelView,
			showModal: showStepPanel,
			closeModal: closeStepPanel,
		} = useAsyncModal('', editStepViewLoader);
		const selectedStepId = ref('');
		const openEditStepModal = (opener: FocusableElement, stepId: string, partId: string) => {
			selectedStepId.value = stepId;
			selectedPartId.value = partId;
			showStepPanel(opener);
		};

		// Requesting a sample certificate
		const {
			track: trackSampleCertificateRequest,
			isLoading: isRequestingSampleCertificate,
			isSuccess: successfullyRequestedSampleCertificate,
			isError: errorRequestingSampleCertificate,
		} = useAsyncStatus();
		const requestSampleCertificate = () => {
			trackSampleCertificateRequest(lmsAdminApi.sendSampleCertificate(program.value.id));
		};

		return {
			program,
			isLoadingPage,
			isErrorPage,
			toLearningPrograms: { name: Routes.GROUP_ADMIN_LEARNING_PROGRAMS_LIST },
			publishInfoPosition: DropdownPosition.BOTTOM_RIGHT,
			showUnpublishedModuleWarning,
			// Editing the Program
			isEditProgramPanelVisible,
			editProgramView,
			openEditProgramModal,
			closeEditProgramModal,
			// Program updates
			isAnyLoading,
			maxUpdateDate,
			// Publish Info
			updateProgramDates,
			isDateLoading,
			isDateError,
			isPublishPending,
			cancelPendingPublish,
			completePendingPublishProgramAndModules,
			completePendingPublishProgramOnly,
			// Module ordering
			handleDragInput,
			// Editing Modules
			isEditModulePanelVisible,
			openEditModuleModal,
			closeEditModuleModal,
			editModuleView,
			// Adding
			addModule,
			deleteModule,
			moduleButton: Size.LARGE,
			// Selecting
			selectedModuleId,
			isSelectedModuleId,
			onUnchoose,
			// Parts editing
			selectedPartId,
			openEditPartModal,
			isPartPanelVisible,
			partPanelView,
			showPartPanel,
			closePartPanel,
			// Step editing
			isStepPanelVisible,
			stepPanelView,
			closeStepPanel,
			selectedStepId,
			openEditStepModal,
			// Draggable
			moveInternal,
			// Sample certificate
			requestSampleCertificateButtonColor: ButtonPalettes.PRIMARY,
			requestSampleCertificateButtonSize: Size.INLINE,
			isRequestingSampleCertificate,
			successfullyRequestedSampleCertificate,
			errorRequestingSampleCertificate,
			requestSampleCertificate,
			// Util
			Size,
			Spacing,
			SETTINGS_ICON,
			ButtonPalettes,
		};
	},
});
